import escudo from '../images/escudo.png';
import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

import { apiUrl } from '../config/api';
const mainNavItems = [
    {
        id: "historial",
        title: "Historial",
        to: "/historial",
    },
    {
        id: "records",
        title: "Récords",
        to: "/records",
    },
];

const partidosItems = [
    {
        id: "buscarpartido",
        title: "Buscar partido",
        to: "/partidos/buscarpartido",
    },
    {
        id: "playoffs",
        title: "Playoffs",
        to: "/partidos/playoffs",
    },
];

const perfilesItems = [
    {
        id: "jugadores",
        title: "Jugadores",
        to: "/perfiles/jugadores",
    },
    {
        id: "tecnicos",
        title: "Técnicos",
        to: "/perfiles/tecnicos",
    },
    {
        id: "planteles",
        title: "Planteles",
        to: "/perfiles/planteles",
    },
    {
        id: "comparar",
        title: "Comparar",
        to: "/perfiles/comparar",
    },
];

const sobreClubItems = [
    {
        id: "fundacion",
        title: "Fundación",
        to: "/historia/fundacion",
    },
    {
        id: "canchas",
        title: "Canchas",
        to: "/historia/canchas",
    },
    {
        id: "estrellas",
        title: "Estrellas",
        to: "/historia/estrellas",
    },
    {
        id: "presidentes",
        title: "Presidentes",
        to: "/historia/presidentes",
    },
    {
        id: "camisetas",
        title: "Camisetas",
        to: "/historia/camisetas",
    },
];

const parseCount = (value) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : 0;
};

const getTournamentCounts = (item = {}) => {
    const nestedCounts = item?.cantidadTorneos || item?.conteoTorneos || item?.torneosCount || item?.torneos || {};
    const lubCount = parseCount(
        item?.lubCount ??
        item?.cantidadLUB ??
        item?.lub ??
        nestedCounts?.lub ??
        nestedCounts?.cantidadLUB
    );
    const internationalCount = parseCount(
        item?.internationalCount ??
        item?.internacionalesCount ??
        item?.cantidadInternacionales ??
        item?.internacionales ??
        nestedCounts?.internacionales ??
        nestedCounts?.internationales ??
        nestedCounts?.cantidadInternacionales
    );

    return { lubCount, internationalCount };
};

const Navbar = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [openDropdown, setOpenDropdown] = useState(null);
    const [hoveredDropdown, setHoveredDropdown] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileOpenSection, setMobileOpenSection] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const searchDataRef = useRef(null);
    const searchInputRef = useRef(null);

    const getTournamentSummary = (item) => {
        const { lubCount, internationalCount } = getTournamentCounts(item);
        if (lubCount === 0 && internationalCount === 0) return '';
        return `LUB (${lubCount}) - Internacionales (${internationalCount})`;
    };

    const loadSearchData = useCallback(async () => {
        if (searchDataRef.current) return;
        setSearchLoading(true);
        try {
            const [jRes, tRes] = await Promise.all([
                fetch(apiUrl('listado/listadoJugadores')),
                fetch(apiUrl('listado/listadoTecnicos')),
            ]);
            const [jData, tData] = await Promise.all([jRes.json(), tRes.json()]);
            const normalizePlayers = (arr) => {
                if (!Array.isArray(arr)) return [];
                return arr
                    .map((item) => {
                        if (typeof item === 'string') {
                            const nombre = item.trim();
                            return nombre ? { nombre, lubCount: 0, internationalCount: 0 } : null;
                        }

                        const nombre = String(item?.jugador || item?.nombre || '').trim();
                        if (!nombre) return null;

                        const counts = getTournamentCounts(item);

                        return {
                            nombre,
                            lubCount: counts.lubCount,
                            internationalCount: counts.internationalCount,
                        };
                    })
                    .filter(Boolean)
                    .sort((a, b) => {
                        return a.nombre.localeCompare(b.nombre, 'es');
                    });
            };
            const normalizeArr = (arr, type) => {
                if (!Array.isArray(arr)) return [];
                return arr
                    .map((item) => {
                        if (typeof item === 'string') {
                            const nombre = item.trim();
                            return nombre ? { nombre, lubCount: 0, internationalCount: 0 } : null;
                        }

                        const nombre = String(
                            type === 'tecnico'
                                ? item?.tecnico || item?.nombre || item?.jugador || ''
                                : item?.jugador || item?.nombre || ''
                        ).trim();

                        if (!nombre) return null;

                        const counts = getTournamentCounts(item);

                        return {
                            nombre,
                            lubCount: counts.lubCount,
                            internationalCount: counts.internationalCount,
                        };
                    })
                    .filter(Boolean)
                    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
            };
            searchDataRef.current = {
                jugadores: normalizePlayers(jData),
                tecnicos: normalizeArr(tData, 'tecnico'),
            };
        } catch {
            searchDataRef.current = { jugadores: [], tecnicos: [] };
        } finally {
            setSearchLoading(false);
        }
    }, []);

    const handleOpenSearch = useCallback(() => {
        setSearchOpen(true);
        setMobileMenuOpen(false);
        setMobileOpenSection(null);
        setSearchQuery('');
        loadSearchData();
    }, [loadSearchData]);

    const handleCloseSearch = useCallback(() => {
        setSearchOpen(false);
        setSearchQuery('');
    }, []);

    useEffect(() => {
        if (searchOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [searchOpen]);

    useEffect(() => {
        if (!searchOpen) return;
        const handler = (e) => { if (e.key === 'Escape') handleCloseSearch(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [searchOpen, handleCloseSearch]);

    useEffect(() => { setActiveIndex(-1); }, [searchQuery]);

    const normalizeStr = (str) =>
        String(str || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const query = searchQuery.trim();
    const searchResults = (() => {
        if (!query || !searchDataRef.current) return null;
        const nq = normalizeStr(query);
        const jugadores = searchDataRef.current.jugadores.filter((jugador) => normalizeStr(jugador?.nombre).includes(nq)).slice(0, 6);
        const tecnicos = searchDataRef.current.tecnicos.filter((tecnico) => normalizeStr(tecnico?.nombre).includes(nq)).slice(0, 6);
        return { jugadores, tecnicos };
    })();

    const allResults = searchResults
        ? [
            ...searchResults.jugadores.map((jugador) => ({ nombre: jugador.nombre })),
            ...searchResults.tecnicos.map((tecnico) => ({ nombre: tecnico.nombre })),
        ]
        : [];

    const renderSearchResult = (item, index, dotClass) => (
        <button
            key={item.nombre}
            type="button"
            onClick={() => handleResultClick(item.nombre)}
            className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${activeIndex === index ? 'text-white bg-white/10' : 'text-slate-300 hover:text-white hover:bg-white/5'}`}
        >
            <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${dotClass}`}></span>
            <span className="min-w-0 flex-1 text-left">
                <span className="block truncate">{item.nombre}</span>
                {getTournamentSummary(item) ? (
                    <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 truncate">
                        {getTournamentSummary(item)}
                    </span>
                ) : null}
            </span>
        </button>
    );

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(i => Math.min(i + 1, allResults.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, -1));
        } else if (e.key === 'Enter' && activeIndex >= 0) {
            e.preventDefault();
            handleResultClick(allResults[activeIndex].nombre);
        }
    };

    const handleResultClick = (nombre) => {
        navigate(`/jugador/${nombre}`);
        handleCloseSearch();
        setMobileMenuOpen(false);
        setMobileOpenSection(null);
    };

    const navLinkClass = ({ isActive }) => `text-sm lg:text-base border-b-2 pb-1 !no-underline hover:!no-underline focus:!no-underline ${isActive ? "text-green-500 border-red-700" : "text-slate-300 hover:text-white border-transparent"}`;
    const dropdownItemClass = ({ isActive }) => `block px-4 py-2 text-sm !no-underline hover:!no-underline focus:!no-underline ${isActive ? "text-green-500" : "text-slate-300 hover:text-white"}`;
    const isDropdownOpen = (dropdownId) => openDropdown === dropdownId || hoveredDropdown === dropdownId;
    const isDropdownRouteActive = (items) => items.some((item) => pathname.startsWith(item.to));
    const isPerfilesRouteActive = isDropdownRouteActive(perfilesItems) || /^\/jugador(\/|$)/.test(pathname);
    const handleDropdownToggle = (dropdownId) => {
        setOpenDropdown((currentDropdown) => (currentDropdown === dropdownId ? null : dropdownId));
    };
    const handleDropdownItemClick = () => {
        setOpenDropdown(null);
        setHoveredDropdown(null);
    };
    const handleMobileLinkClick = () => {
        setMobileMenuOpen(false);
        setMobileOpenSection(null);
        handleCloseSearch();
    };
    const handleMobileSectionToggle = (section) => {
        setMobileOpenSection((current) => (current === section ? null : section));
    };
    const handleMobileMenuToggle = () => {
        setMobileMenuOpen((prev) => {
            if (!prev) {
                handleCloseSearch();
                loadSearchData();
            } else {
                setSearchQuery('');
            }
            return !prev;
        });
    };

    return (
        <>
        {/* Backdrop – outside nav so it truly covers the whole page */}
        <div
            className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${(searchOpen || mobileMenuOpen) ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            onClick={() => {
                if (searchOpen) handleCloseSearch();
                if (mobileMenuOpen) {
                    setMobileMenuOpen(false);
                    setSearchQuery('');
                    setMobileOpenSection(null);
                }
            }}
            aria-hidden="true"
        />
        <nav className="fixed top-0 w-full z-50 bg-[#062a1a]/95 backdrop-blur-md border-b border-white/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    <Link to='/' className="flex items-center gap-3 no-underline hover:no-underline focus:no-underline active:no-underline" onClick={handleMobileLinkClick}>
                        <img src={escudo} className="h-10 w-auto" alt="Escudo de Aguada" />
                        <span className="text-xl tracking-wider text-green-500 font-semibold">
                            AGUADA <span className="text-red-700">STATS</span>
                        </span>
                    </Link>

                    {/* Desktop menu */}
                    <div className="hidden md:flex items-center space-x-6 lg:space-x-8 font-medium">
                        {mainNavItems.map((item) => (
                            <NavLink
                                key={item.id}
                                to={item.to}
                                className={navLinkClass}
                            >
                                {item.title}
                            </NavLink>
                        ))}

                        <div
                            className="relative"
                            onMouseEnter={() => setHoveredDropdown('partidos')}
                            onMouseLeave={() => setHoveredDropdown(null)}
                        >
                            <button
                                type="button"
                                onClick={() => handleDropdownToggle('partidos')}
                                aria-expanded={isDropdownOpen('partidos')}
                                className={`text-sm lg:text-base border-b-2 pb-1 ${isDropdownRouteActive(partidosItems) ? 'text-green-500 border-red-700' : 'text-slate-300 hover:text-white border-transparent'}`}
                            >
                                Partidos
                            </button>
                            <div className={`absolute left-0 top-full mt-1 w-44 rounded-md border border-white/10 bg-[#062a1a]/95 backdrop-blur-md transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] z-50 ${isDropdownOpen('partidos') ? 'opacity-100 visible translate-y-0 scale-100' : 'opacity-0 invisible -translate-y-3 scale-95'}`}>
                                {partidosItems.map((item) => (
                                    <NavLink
                                        key={item.id}
                                        to={item.to}
                                        className={dropdownItemClass}
                                        onClick={handleDropdownItemClick}
                                    >
                                        {item.title}
                                    </NavLink>
                                ))}
                            </div>
                        </div>

                        <div
                            className="relative"
                            onMouseEnter={() => setHoveredDropdown('perfiles')}
                            onMouseLeave={() => setHoveredDropdown(null)}
                        >
                            <button
                                type="button"
                                onClick={() => handleDropdownToggle('perfiles')}
                                aria-expanded={isDropdownOpen('perfiles')}
                                className={`text-sm lg:text-base border-b-2 pb-1 ${isPerfilesRouteActive ? 'text-green-500 border-red-700' : 'text-slate-300 hover:text-white border-transparent'}`}
                            >
                                Perfiles
                            </button>
                            <div className={`absolute left-0 top-full mt-1 w-44 rounded-md border border-white/10 bg-[#062a1a]/95 backdrop-blur-md transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] z-50 ${isDropdownOpen('perfiles') ? 'opacity-100 visible translate-y-0 scale-100' : 'opacity-0 invisible -translate-y-3 scale-95'}`}>
                                {perfilesItems.map((item) => (
                                    <NavLink
                                        key={item.id}
                                        to={item.to}
                                        className={dropdownItemClass}
                                        onClick={handleDropdownItemClick}
                                    >
                                        {item.title}
                                    </NavLink>
                                ))}
                            </div>
                        </div>

                        <div
                            className="relative"
                            onMouseEnter={() => setHoveredDropdown('sobreClub')}
                            onMouseLeave={() => setHoveredDropdown(null)}
                        >
                            <button
                                type="button"
                                onClick={() => handleDropdownToggle('sobreClub')}
                                aria-expanded={isDropdownOpen('sobreClub')}
                                className={`text-sm lg:text-base border-b-2 pb-1 ${isDropdownRouteActive(sobreClubItems) ? 'text-green-500 border-red-700' : 'text-slate-300 hover:text-white border-transparent'}`}
                            >
                                Historia
                            </button>
                            <div className={`absolute left-0 top-full mt-1 w-44 rounded-md border border-white/10 bg-[#062a1a]/95 backdrop-blur-md transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] z-50 ${isDropdownOpen('sobreClub') ? 'opacity-100 visible translate-y-0 scale-100' : 'opacity-0 invisible -translate-y-3 scale-95'}`}>
                                {sobreClubItems.map((item) => (
                                    <NavLink
                                        key={item.id}
                                        to={item.to}
                                        className={dropdownItemClass}
                                        onClick={handleDropdownItemClick}
                                    >
                                        {item.title}
                                    </NavLink>
                                ))}
                            </div>
                        </div>

                        {/* Search icon – desktop */}
                        <button
                            type="button"
                            onClick={searchOpen ? handleCloseSearch : handleOpenSearch}
                            aria-label={searchOpen ? 'Cerrar buscador' : 'Abrir buscador'}
                            className={`text-sm lg:text-base border-b-2 pb-1 transition-colors ${searchOpen ? 'text-green-500 border-red-700' : 'text-slate-300 hover:text-white border-transparent'}`}
                        >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </button>
                    </div>

                    {/* Mobile: hamburger */}
                    <button
                        type="button"
                        className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-slate-300 hover:text-white focus:outline-none"
                        aria-expanded={mobileMenuOpen}
                        aria-label="Abrir menú"
                        onClick={handleMobileMenuToggle}
                    >
                        {mobileMenuOpen ? (
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            {/* Search panel */}
            <div className={`grid transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${searchOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                <div className="overflow-hidden">
                    <div className="border-t border-white/10 bg-[#062a1a]/98 px-4 sm:px-6 lg:px-8 py-4">
                        <div className="max-w-7xl mx-auto">
                            {/* Input row */}
                            <div className="flex items-center gap-2 max-w-sm md:max-w-md">
                                    <div className="relative flex items-center flex-1">
                                        <svg className="absolute left-3 h-4 w-4 text-slate-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            placeholder="Buscar jugadores o técnicos..."
                                            className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-8 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-green-500/60 transition-colors"
                                        />
                                        {searchQuery && (
                                            <button
                                                type="button"
                                                onClick={() => setSearchQuery('')}
                                                className="absolute right-3 text-slate-500 hover:text-slate-300 transition-colors"
                                                aria-label="Limpiar búsqueda"
                                            >
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleCloseSearch}
                                        className="shrink-0 p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                                        aria-label="Cerrar buscador"
                                    >
                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Loading */}
                                {searchLoading && (
                                    <p className="text-slate-500 text-xs mt-3">Cargando datos...</p>
                                )}

                                {/* Results */}
                                {!searchLoading && searchResults && (
                                    <div className="mt-3">
                                        {searchResults.jugadores.length === 0 && searchResults.tecnicos.length === 0 ? (
                                            <p className="text-slate-500 text-sm">Sin resultados para <span className="text-slate-300">"{searchQuery}"</span></p>
                                        ) : (
                                            <div className="md:grid md:grid-cols-2 md:gap-x-8 space-y-4 md:space-y-0">
                                                {searchResults.jugadores.length > 0 && (
                                                    <div>
                                                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Jugadores</p>
                                                        <div className="space-y-0.5">
                                                            {searchResults.jugadores.map((jugador, i) => (
                                                                <button
                                                                    key={jugador.nombre}
                                                                    type="button"
                                                                    onClick={() => handleResultClick(jugador.nombre)}
                                                                    className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${activeIndex === i ? 'text-white bg-white/10' : 'text-slate-300 hover:text-white hover:bg-white/5'}`}
                                                                >
                                                                    <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                                                    <span className="min-w-0 flex-1 text-left">
                                                                        <span className="block truncate">{jugador.nombre}</span>
                                                                        {getTournamentSummary(jugador) ? (
                                                                            <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 truncate">
                                                                                {getTournamentSummary(jugador)}
                                                                            </span>
                                                                        ) : null}
                                                                    </span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {searchResults.tecnicos.length > 0 && (
                                                    <div>
                                                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Técnicos</p>
                                                        <div className="space-y-0.5">
                                                            {searchResults.tecnicos.map((tecnico, i) => renderSearchResult(tecnico, searchResults.jugadores.length + i, 'bg-red-600'))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Hint */}
                                {!searchLoading && !searchResults && (
                                    <p className="text-slate-600 text-xs mt-3">Podés buscar por nombre de jugador o técnico</p>
                                )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile menu panel */}
            <div className={`md:hidden grid transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${mobileMenuOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                <div className="overflow-hidden">
                    <div className="border-t border-white/10 bg-[#062a1a]/98">
                        <div className="px-4 py-3 space-y-1 font-medium" onClick={() => { if (searchQuery) setSearchQuery(''); }}>
                            {/* Search inside mobile menu */}
                            <div className="pb-3 mb-1 border-b border-white/10">
                                <div className="relative flex items-center">
                                    <svg className="absolute left-3 h-4 w-4 text-slate-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Buscar jugadores o técnicos..."
                                        className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-8 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-green-500/60 transition-colors"
                                    />
                                    {searchQuery && (
                                        <button
                                            type="button"
                                            onClick={() => setSearchQuery('')}
                                            className="absolute right-3 text-slate-500 hover:text-slate-300 transition-colors"
                                            aria-label="Limpiar búsqueda"
                                        >
                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                {searchLoading && (
                                    <p className="text-slate-500 text-xs mt-2">Cargando datos...</p>
                                )}
                                {!searchLoading && searchResults && (
                                    <div className="mt-2 space-y-3">
                                        {searchResults.jugadores.length === 0 && searchResults.tecnicos.length === 0 ? (
                                            <p className="text-slate-500 text-sm px-1">Sin resultados para <span className="text-slate-300">"{searchQuery}"</span></p>
                                        ) : (
                                            <>
                                                {searchResults.jugadores.length > 0 && (
                                                    <div>
                                                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-1 mb-1">Jugadores</p>
                                                        <div className="space-y-0.5">
                                                            {searchResults.jugadores.map((jugador, i) => (
                                                                <button
                                                                    key={jugador.nombre}
                                                                    type="button"
                                                                    onClick={() => handleResultClick(jugador.nombre)}
                                                                    className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${activeIndex === i ? 'text-white bg-white/10' : 'text-slate-300 hover:text-white hover:bg-white/5'}`}
                                                                >
                                                                    <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                                                    <span className="min-w-0 flex-1 text-left">
                                                                        <span className="block truncate">{jugador.nombre}</span>
                                                                        {getTournamentSummary(jugador) ? (
                                                                            <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 truncate">
                                                                                {getTournamentSummary(jugador)}
                                                                            </span>
                                                                        ) : null}
                                                                    </span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {searchResults.tecnicos.length > 0 && (
                                                    <div>
                                                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-1 mb-1">Técnicos</p>
                                                        <div className="space-y-0.5">
                                                            {searchResults.tecnicos.map((tecnico, i) => renderSearchResult(tecnico, searchResults.jugadores.length + i, 'bg-red-600'))}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Nav items – dimmed while searching */}
                            <div className={`transition-opacity duration-200 ${searchQuery ? 'opacity-25 pointer-events-none' : 'opacity-100'}`}>
                            {mainNavItems.map((item) => (
                                <NavLink
                                    key={item.id}
                                    to={item.to}
                                    onClick={handleMobileLinkClick}
                                    className={({ isActive }) =>
                                        `block px-3 py-2 rounded-md text-sm !no-underline hover:!no-underline ${isActive ? "text-green-500 bg-white/5" : "text-slate-300 hover:text-white hover:bg-white/5"}`
                                    }
                                >
                                    {item.title}
                                </NavLink>
                            ))}

                            {/* Partidos accordion */}
                            <div>
                                <button
                                    type="button"
                                    onClick={() => handleMobileSectionToggle('partidos')}
                                    className={`w-full flex justify-between items-center px-3 py-2 rounded-md text-sm ${isDropdownRouteActive(partidosItems) ? 'text-green-500' : 'text-slate-300 hover:text-white hover:bg-white/5'}`}
                                >
                                    Partidos
                                    <svg
                                        className={`h-4 w-4 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${mobileOpenSection === 'partidos' ? 'rotate-180' : ''}`}
                                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                <div className={`grid transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${mobileOpenSection === 'partidos' ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                                    <div className="overflow-hidden">
                                        <div className="mt-1 ml-3 border-l border-white/10 pl-3 space-y-1 pb-1">
                                            {partidosItems.map((item) => (
                                                <NavLink
                                                    key={item.id}
                                                    to={item.to}
                                                    onClick={handleMobileLinkClick}
                                                    className={({ isActive }) =>
                                                        `block px-3 py-2 rounded-md text-sm !no-underline hover:!no-underline ${isActive ? "text-green-500" : "text-slate-300 hover:text-white hover:bg-white/5"}`
                                                    }
                                                >
                                                    {item.title}
                                                </NavLink>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Perfiles accordion */}
                            <div>
                                <button
                                    type="button"
                                    onClick={() => handleMobileSectionToggle('perfiles')}
                                    className={`w-full flex justify-between items-center px-3 py-2 rounded-md text-sm ${isPerfilesRouteActive ? 'text-green-500' : 'text-slate-300 hover:text-white hover:bg-white/5'}`}
                                >
                                    Perfiles
                                    <svg
                                        className={`h-4 w-4 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${mobileOpenSection === 'perfiles' ? 'rotate-180' : ''}`}
                                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                <div className={`grid transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${mobileOpenSection === 'perfiles' ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                                    <div className="overflow-hidden">
                                        <div className="mt-1 ml-3 border-l border-white/10 pl-3 space-y-1 pb-1">
                                            {perfilesItems.map((item) => (
                                                <NavLink
                                                    key={item.id}
                                                    to={item.to}
                                                    onClick={handleMobileLinkClick}
                                                    className={({ isActive }) =>
                                                        `block px-3 py-2 rounded-md text-sm !no-underline hover:!no-underline ${isActive ? "text-green-500" : "text-slate-300 hover:text-white hover:bg-white/5"}`
                                                    }
                                                >
                                                    {item.title}
                                                </NavLink>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Historia accordion */}
                            <div>
                                <button
                                    type="button"
                                    onClick={() => handleMobileSectionToggle('sobreClub')}
                                    className={`w-full flex justify-between items-center px-3 py-2 rounded-md text-sm ${isDropdownRouteActive(sobreClubItems) ? 'text-green-500' : 'text-slate-300 hover:text-white hover:bg-white/5'}`}
                                >
                                    Historia
                                    <svg
                                        className={`h-4 w-4 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${mobileOpenSection === 'sobreClub' ? 'rotate-180' : ''}`}
                                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                <div className={`grid transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${mobileOpenSection === 'sobreClub' ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                                    <div className="overflow-hidden">
                                        <div className="mt-1 ml-3 border-l border-white/10 pl-3 space-y-1 pb-1">
                                            {sobreClubItems.map((item) => (
                                                <NavLink
                                                    key={item.id}
                                                    to={item.to}
                                                    onClick={handleMobileLinkClick}
                                                    className={({ isActive }) =>
                                                        `block px-3 py-2 rounded-md text-sm !no-underline hover:!no-underline ${isActive ? "text-green-500" : "text-slate-300 hover:text-white hover:bg-white/5"}`
                                                    }
                                                >
                                                    {item.title}
                                                </NavLink>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </div>{/* end dimmed nav items */}
                        </div>
                    </div>
                </div>
            </div>
        </nav>
        </>
    );
};

export default Navbar;

